import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { 
  Mail, Server, Lock, User, Send, CheckCircle, XCircle, 
  RefreshCw, Eye, EyeOff, Shield, AlertTriangle, Info
} from 'lucide-react';
import api from '../../lib/axios';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const CMSSmtpConfigPage = () => {
  const [config, setConfig] = useState({
    smtp_host: '',
    smtp_port: 465,
    smtp_user: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_from_name: '',
    smtp_use_ssl: true,
    smtp_use_tls: false,
    configured: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testRecipient, setTestRecipient] = useState('');

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const res = await api.get('/cms/smtp-config');
      setConfig(res.data);
    } catch (error) {
      console.error('Error loading SMTP config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/cms/smtp-config', config);
      toast.success('Configuration SMTP enregistrée');
      loadConfig();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testRecipient) {
      toast.error('Veuillez saisir un email de destinataire');
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const res = await api.post('/cms/smtp-config/test', { ...config, test_recipient: testRecipient });
      setTestResult(res.data);
      if (res.data.success) {
        toast.success('Email de test envoyé !');
      } else {
        toast.error(res.data.error || 'Erreur lors du test');
      }
    } catch (error) {
      setTestResult({ success: false, error: error.response?.data?.detail || 'Erreur de connexion' });
      toast.error('Erreur lors du test');
    } finally {
      setTesting(false);
    }
  };

  const presetConfigs = [
    { name: 'PlanetHoster', host: 'mail.votre-domaine.com', port: 465, ssl: true, tls: false },
    { name: 'OVH', host: 'ssl0.ovh.net', port: 465, ssl: true, tls: false },
    { name: 'Gmail', host: 'smtp.gmail.com', port: 465, ssl: true, tls: false },
    { name: 'Outlook', host: 'smtp.office365.com', port: 587, ssl: false, tls: true },
    { name: 'Sendinblue', host: 'smtp-relay.sendinblue.com', port: 587, ssl: false, tls: true },
    { name: 'SendGrid', host: 'smtp.sendgrid.net', port: 587, ssl: false, tls: true }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Configuration SMTP</h1>
          <p className="text-slate-500">
            Configurez l&apos;envoi d&apos;emails pour les notifications et formulaires
          </p>
        </div>
        <Badge variant={config.configured ? "default" : "secondary"} className={cn(
          "w-fit",
          config.configured ? "bg-green-100 text-green-700" : "bg-orange-100 text-orange-700"
        )}>
          {config.configured ? (
            <><CheckCircle className="w-3 h-3 mr-1" /> Configuré</>
          ) : (
            <><AlertTriangle className="w-3 h-3 mr-1" /> Non configuré</>
          )}
        </Badge>
      </div>

      {/* Info Alert */}
      <Alert className="border-blue-200 bg-blue-50">
        <Info className="h-4 w-4 text-blue-500" />
        <AlertDescription className="text-blue-800">
          La configuration SMTP permet d&apos;envoyer des emails depuis votre propre domaine pour les demandes de devis, 
          rappels et notifications. Sans configuration, les emails ne seront pas envoyés.
        </AlertDescription>
      </Alert>

      {/* Presets */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Configuration rapide</CardTitle>
          <CardDescription>Sélectionnez votre fournisseur email pour pré-remplir les paramètres</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {presetConfigs.map((preset) => (
              <Button
                key={preset.name}
                variant="outline"
                size="sm"
                onClick={() => setConfig(prev => ({
                  ...prev,
                  smtp_host: preset.host,
                  smtp_port: preset.port,
                  smtp_use_ssl: preset.ssl,
                  smtp_use_tls: preset.tls
                }))}
                className={cn(
                  config.smtp_host === preset.host && "border-cyan-500 bg-cyan-50"
                )}
              >
                {preset.name}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Configuration Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Server className="w-5 h-5 text-cyan-500" />
            Paramètres du serveur SMTP
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Server Settings */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="smtp_host">Serveur SMTP</Label>
                <Input
                  id="smtp_host"
                  placeholder="smtp.example.com"
                  value={config.smtp_host}
                  onChange={(e) => setConfig(prev => ({ ...prev, smtp_host: e.target.value }))}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="smtp_port">Port</Label>
                <Input
                  id="smtp_port"
                  type="number"
                  placeholder="587"
                  value={config.smtp_port}
                  onChange={(e) => setConfig(prev => ({ ...prev, smtp_port: parseInt(e.target.value) || 465 }))}
                />
                <p className="text-xs text-slate-500">
                  Port 465 = SSL (PlanetHoster, OVH) | Port 587 = TLS (Outlook, SendGrid)
                </p>
              </div>

              {/* SSL/TLS Selection */}
              <div className="space-y-2">
                <Label>Sécurité de la connexion</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setConfig(prev => ({ ...prev, smtp_use_ssl: true, smtp_use_tls: false, smtp_port: 465 }))}
                    className={cn(
                      "flex items-center justify-center gap-2 p-3 rounded-lg border transition-all",
                      config.smtp_use_ssl 
                        ? "border-green-500 bg-green-50 text-green-700" 
                        : "border-slate-200 hover:border-slate-300"
                    )}
                  >
                    <Shield className="w-4 h-4" />
                    <span className="text-sm font-medium">SSL (port 465)</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfig(prev => ({ ...prev, smtp_use_ssl: false, smtp_use_tls: true, smtp_port: 587 }))}
                    className={cn(
                      "flex items-center justify-center gap-2 p-3 rounded-lg border transition-all",
                      config.smtp_use_tls && !config.smtp_use_ssl
                        ? "border-blue-500 bg-blue-50 text-blue-700" 
                        : "border-slate-200 hover:border-slate-300"
                    )}
                  >
                    <Shield className="w-4 h-4" />
                    <span className="text-sm font-medium">TLS (port 587)</span>
                  </button>
                </div>
                <p className="text-xs text-slate-500">
                  PlanetHoster, OVH, Gmail : utilisez SSL. Outlook, SendGrid : utilisez TLS.
                </p>
              </div>
            </div>

            {/* Authentication */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="smtp_user">Identifiant / Email</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="smtp_user"
                    className="pl-10"
                    placeholder="user@example.com"
                    value={config.smtp_user}
                    onChange={(e) => setConfig(prev => ({ ...prev, smtp_user: e.target.value }))}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="smtp_password">Mot de passe</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="smtp_password"
                    type={showPassword ? "text" : "password"}
                    className="pl-10 pr-10"
                    placeholder="••••••••"
                    value={config.smtp_password}
                    onChange={(e) => setConfig(prev => ({ ...prev, smtp_password: e.target.value }))}
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-slate-500">
                  Pour Gmail, utilisez un mot de passe d&apos;application
                </p>
              </div>
            </div>
          </div>

          {/* From Settings */}
          <div className="border-t pt-6">
            <h3 className="font-medium text-slate-900 mb-4 flex items-center gap-2">
              <Mail className="w-4 h-4 text-cyan-500" />
              Informations d&apos;expéditeur
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="smtp_from_name">Nom d&apos;expéditeur</Label>
                <Input
                  id="smtp_from_name"
                  placeholder="MSC L'Agence"
                  value={config.smtp_from_name}
                  onChange={(e) => setConfig(prev => ({ ...prev, smtp_from_name: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="smtp_from_email">Email d&apos;expéditeur</Label>
                <Input
                  id="smtp_from_email"
                  type="email"
                  placeholder="contact@msc-agence.fr"
                  value={config.smtp_from_email}
                  onChange={(e) => setConfig(prev => ({ ...prev, smtp_from_email: e.target.value }))}
                />
              </div>
            </div>
          </div>

          {/* Test Result */}
          {testResult && (
            <Alert className={cn(
              "mt-4",
              testResult.success 
                ? "border-green-200 bg-green-50" 
                : "border-red-200 bg-red-50"
            )}>
              {testResult.success ? (
                <CheckCircle className="h-4 w-4 text-green-500" />
              ) : (
                <XCircle className="h-4 w-4 text-red-500" />
              )}
              <AlertDescription className={testResult.success ? "text-green-800" : "text-red-800"}>
                {testResult.success ? testResult.message : testResult.error}
              </AlertDescription>
            </Alert>
          )}

          {/* Actions */}
          <div className="space-y-4 pt-4 border-t">
            {/* Test Email Section */}
            <div className="flex flex-col sm:flex-row gap-3 items-end">
              <div className="flex-1 space-y-2">
                <Label htmlFor="test_recipient">Email de test (destinataire)</Label>
                <Input
                  id="test_recipient"
                  type="email"
                  placeholder="votre@email.com"
                  value={testRecipient}
                  onChange={(e) => setTestRecipient(e.target.value)}
                />
              </div>
              <Button 
                variant="outline"
                onClick={handleTest} 
                disabled={testing || !config.smtp_host || !config.smtp_user || !testRecipient}
                className="w-full sm:w-auto"
              >
                {testing ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Envoyer un email de test
              </Button>
            </div>
            
            {/* Save Button */}
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="w-full sm:w-auto"
            >
              {saving ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Enregistrer la configuration
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Help Section */}
      <Card className="border-slate-200 bg-slate-50">
        <CardHeader>
          <CardTitle className="text-lg">Aide à la configuration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm text-slate-600">
          <div>
            <h4 className="font-medium text-slate-900 mb-1">Gmail</h4>
            <p>
              Activez l&apos;authentification à deux facteurs, puis créez un 
              <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:underline ml-1">
                mot de passe d&apos;application
              </a>.
            </p>
          </div>
          <div>
            <h4 className="font-medium text-slate-900 mb-1">OVH / Autres hébergeurs</h4>
            <p>
              Utilisez les paramètres SMTP fournis par votre hébergeur. 
              Le port 587 avec TLS est généralement recommandé.
            </p>
          </div>
          <div>
            <h4 className="font-medium text-slate-900 mb-1">Services d&apos;envoi (SendGrid, Sendinblue)</h4>
            <p>
              Ces services offrent une meilleure délivrabilité. 
              Utilisez la clé API comme mot de passe.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CMSSmtpConfigPage;
